/**
 * Helper functions for task-related operations
 */

import type { Task } from '../../../types/task'

export enum TaskStatus {
  NOT_STARTED = 'Not Started',
  IN_PROGRESS = 'In Progress',
  ON_HOLD = 'On Hold',
  COMPLETED = 'Completed',
  CANCELED = 'Canceled',
}

export const OPEN_TASK_STATUSES: string[] = [
  TaskStatus.NOT_STARTED,
  TaskStatus.IN_PROGRESS,
  TaskStatus.ON_HOLD,
]

export const CLOSED_TASK_STATUSES: string[] = [
  TaskStatus.COMPLETED,
  TaskStatus.CANCELED,
]

/**
 * Get case ID from task links
 * @param task - Task object
 * @returns Case ID or null if not found
 */
export function getTaskCase(task: Task): string | null {
  const caseLink = task.links?.find(link => link.rel === 'case')
  return caseLink?.id || null
}

/**
 * Get team ID from task - checks both owner_team_id field and links array
 * Priority: rel='team' link (most recent update) > owner_team_id (original value)
 * @param task - Task object
 * @returns Team ID or null if not found
 */
export function getTaskTeam(task: Task): string | null {
  // First priority: Check links array with rel='team' (this is what we update in the UI)
  const teamLink = task.links?.find(link => link.rel === 'team')
  if (teamLink?.id) {
    return teamLink.id
  }
  
  // Second priority: Check root-level owner_team_id field
  const taskWithOwner = task as any
  if (taskWithOwner.owner_team_id) {
    return taskWithOwner.owner_team_id
  }
  
  // Third priority: Check for owner_team_id within any link object (commonly in case link)
  const linkWithOwner = task.links?.find((link: any) => link.owner_team_id)
  if (linkWithOwner && (linkWithOwner as any).owner_team_id) {
    return (linkWithOwner as any).owner_team_id
  }
  
  return null
}

/**
 * Get user ID from task - checks links array for user assignment
 * @param task - Task object
 * @returns User ID or null if not found
 */
export function getTaskUser(task: Task): string | null {
  // First priority: Check links array with rel='user'
  const userLink = task.links?.find(link => link.rel === 'user')
  if (userLink?.id) {
    return userLink.id
  }
  
  // Second priority: Check for owner_user_id within the case link object
  const caseLink = task.links?.find(link => link.rel === 'case') as any
  if (caseLink?.owner_user_id) {
    return caseLink.owner_user_id
  }
  
  return null
}

/**
 * Determine if a task has no user assignment
 * @param task - Task object
 * @returns true when no owner user is set
 */
export function isTaskUnassigned(task: Task): boolean {
  const userId = getTaskUser(task)
  return !userId || userId.trim() === ''
}

/**
 * Determine if a task is assigned to a specific user
 * @param task - Task object
 * @param userId - The user ID to compare against
 * @returns true when the task is assigned to the given user ID
 */
export function isTaskAssignedToUser(task: Task, userId: string | undefined | null): boolean {
  if (!userId || typeof userId !== 'string' || userId.trim() === '') {
    return false
  }

  const taskUserId = getTaskUser(task)
  return taskUserId?.trim() === userId.trim()
}

/**
 * Filter a list of tasks to only those effectively owned by the given user.
 *
 * Priority rule (mirrors getTaskUser):
 *   1. If a rel:user link exists → its id is the owner. Include only when it matches userId.
 *   2. If no rel:user link exists → fall back to links.case.owner_user_id.
 *
 * This is needed after a merged search (dual API query) to prevent tasks whose
 * explicit rel:user link points to someone else from appearing in "my tasks" views
 * just because their case.owner_user_id happens to match.
 *
 * TODO: Once the API supports compound link filtering (e.g. an explicit OR across
 * link fields with proper per-object matching), the dual-request + merge pattern in
 * tasks.ts and useTasksApi.ts (searchTasksMerged) can be replaced with a single API
 * query and this client-side filter can be removed entirely.
 */
export function filterTasksByOwner(tasks: Task[], userId: string): Task[] {
  return tasks.filter(task => isTaskAssignedToUser(task, userId))
}

/**
 * Filter a list of tasks to only those that are unassigned to any specific user.
 *
 * A task is considered unassigned when:
 *   - There is no rel:user link in the links array, AND
 *   - There is no non-null owner_user_id on any link (e.g. the case link)
 *
 * Used after a merged dual-request search for the team-tasks view to prevent
 * tasks that are already assigned to a specific user from appearing in the
 * "My Team's Unassigned Tasks" list.
 */
export function filterTasksByTeamUnassigned(tasks: Task[]): Task[] {
  return tasks.filter(task => isTaskUnassigned(task))
}

/**
 * Convert UTC timestamp to local timezone
 * @param utcTimestamp - UTC timestamp string
 * @returns Formatted local date/time string
 */
export function formatUtcTimestamp(utcTimestamp: string): string {
  // Add 'Z' to explicitly mark as UTC if not already present
  const utcString = utcTimestamp.endsWith('Z') ? utcTimestamp : utcTimestamp + 'Z'
  const date = new Date(utcString)
  return date.toLocaleString()
}

/**
 * Format task due date
 * @param dueDate - Due date string
 * @returns Formatted due date string or 'No due date' if empty
 */
export function formatTaskDueDate(dueDate: string): string {
  if (!dueDate) return 'No due date'
  return formatUtcTimestamp(dueDate)
}

/**
 * Get due date status for a task
 * @param dueDate - Due date string
 * @returns 'past-due' | 'due-soon' | 'upcoming' | null
 */
export function getTaskDueDateStatus(dueDate: string | undefined | null): 'past-due' | 'due-soon' | 'upcoming' | null {
  if (!dueDate) return null
  
  const dueDateObj = new Date(dueDate)
  const now = new Date()
  
  // Check if past due
  if (dueDateObj.getTime() < now.getTime()) {
    return 'past-due'
  }
  
  // Check if due within next 24 hours
  const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000)
  if (dueDateObj.getTime() <= twentyFourHoursFromNow.getTime()) {
    return 'due-soon'
  }
  
  return 'upcoming'
}

/**
 * Check if task due date is in the past
 * @param dueDate - Due date string
 * @returns true if past due, false otherwise
 */
export function isTaskDueDatePast(dueDate: string | undefined | null): boolean {
  if (!dueDate) return false
  const dueDateObj = new Date(dueDate)
  const now = new Date()
  return dueDateObj.getTime() < now.getTime()
}

/**
 * Check if task is due within the next 24 hours
 * @param dueDate - Due date string
 * @returns true if due within 24 hours, false otherwise
 */
export function isTaskDueSoon(dueDate: string | undefined | null): boolean {
  if (!dueDate) return false
  const dueDateObj = new Date(dueDate)
  const now = new Date()
  const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000)
  return dueDateObj.getTime() > now.getTime() && dueDateObj.getTime() <= twentyFourHoursFromNow.getTime()
}

/**
 * Get team display name from task
 * @param task - Task object
 * @param partnerTeams - Array of partner teams from store
 * @returns Team display name or fallback
 */
export function getTaskTeamName(task: Task, partnerTeams: any[]): string {
  const teamId = getTaskTeam(task)
  if (!teamId) return '—'
  
  const team = partnerTeams?.find(t => t.val === teamId)
  return team?.lbl || teamId
}

/**
 * Get user display name from task
 * @param task - Task object
 * @param partnerMetaData - Partner metadata from store
 * @returns User display name or fallback
 */
export function getTaskUserName(task: Task, partnerMetaData: any): string {
  const userId = getTaskUser(task)
  if (!userId) return '—'
  
  const user = partnerMetaData?.pl__partners_users?.find(
    (u: any) => u.id === userId
  )
  return user?.lbl || userId
}

/**
 * Get formatted case reference ID from task
 * @param task - Task object
 * @returns Formatted case reference ID (last 6 characters, uppercase) or '—'
 */
export function getTaskCaseReference(task: Task): string {
  const caseId = getTaskCase(task)
  return caseId ? caseId.slice(-6).toUpperCase() : '—'
}

/**
 * Get case route path for navigation
 * @param task - Task object
 * @returns Case route path or null if no case ID
 */
export function getTaskCaseRoute(task: Task): string | null {
  const caseId = getTaskCase(task)
  return caseId ? `/inbox/cases/${caseId}` : null
}

/**
 * Get team and user IDs from task for easier access
 * @param task - Task object
 * @returns Object with teamId and userId
 */
export function getTaskAssignments(task: Task): { teamId: string | null; userId: string | null } {
  return {
    teamId: getTaskTeam(task),
    userId: getTaskUser(task)
  }
}

/**
 * Get task status state for BravoTag component
 * @param task - Task object
 * @returns State value for BravoTag styling
 */
export function getTaskStatusState(task: Task): 'new' | 'ready' | 'waiting' | 'resolved' | 'closed' | undefined {
  if (!task?.status) return undefined
  switch (task.status.toLowerCase()) {
    case TaskStatus.NOT_STARTED.toLowerCase():
      return 'new'
    case TaskStatus.IN_PROGRESS.toLowerCase():
    case 'active':
      return 'ready'
    case TaskStatus.ON_HOLD.toLowerCase():
    case 'waiting':
      return 'waiting'
    case TaskStatus.COMPLETED.toLowerCase():
      return 'resolved'
    case TaskStatus.CANCELED.toLowerCase():
    case 'cancelled':
    case 'closed':
      return 'closed'
    default:
      return undefined
  }
} 